import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';

import { LitigeGestionEtatEnum } from "types/enums";

import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

import Datetime from 'components/Shared/Datetime';
import GeswebLink from 'components/Shared/GeswebLink'

import { fetchPieceGestionFacture } from 'utils/ts-queries';
import { ApiPieceGestionFacture } from 'types/apiReturns';

type Props = {
    numeroPiece: string
}

const InfosFactureLitiges = ({ numeroPiece }: Props) => {


    const [pieceGestion, setPieceGestion] = useState<ApiPieceGestionFacture | null>(null);

    const { isLoading, isError, data } =
        useQuery({
            queryKey: [
                'FactureLitiges',
                numeroPiece
            ],
            queryFn: () => fetchPieceGestionFacture(numeroPiece),
            retry: false,
        });


    useEffect(function dataChanged() {

        if (isLoading) {
            return;
        }
        if (data) {
            setPieceGestion(data);
        }
    }, [data, isLoading]);  


    if (isLoading) {
        return <Skeleton />;
    }

    if (isError) {
        return <span>Erreur de chargement des données de la pièce {numeroPiece}</span>;
    }

    if (!pieceGestion) {
        return <span>Pas de données de la pièce {numeroPiece}</span>;
    }

    return (
        <>

            <Paper elevation={1} sx={{ padding: 1}}>
                <Typography variant="h6" gutterBottom>Facture</Typography>
                <Typography gutterBottom><GeswebLink cle={pieceGestion.facture} structure='facture' mode='button' label={pieceGestion.facture} environment={null} /> du <Datetime datetime={pieceGestion.dateFacturation} /></Typography>
                <Typography gutterBottom>{pieceGestion.facturationNom}</Typography>
                <Typography gutterBottom>{pieceGestion.facturationAdresse1}</Typography>
                <Typography gutterBottom>{pieceGestion.facturationAdresse2}</Typography>
                <Typography gutterBottom>{pieceGestion.facturationCodePostal} {pieceGestion.facturationVille}</Typography>
            </Paper>
            
            {pieceGestion.litiges.length > 0 && (
                <Paper sx={{ mt: 2, p:1 }}>
                    <Typography variant="h6" gutterBottom>Litiges</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Clé</TableCell>
                                    <TableCell>État</TableCell>
                                    <TableCell>Ouverture</TableCell>
                                    <TableCell>Origine</TableCell>
                                    {/* <TableCell>Dossier</TableCell> */}
                                    <TableCell>Commentaire</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>


                                {pieceGestion.litiges.map((litige) => (
                                    <TableRow key={litige.cle}>
                                        <TableCell>
                                            <GeswebLink
                                                cle={litige.cle}
                                                structure='litige'
                                                mode='button'
                                                label={litige.cle}
                                                environment={null}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <span style={{
                                            marginLeft: '8px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: litige.etat === LitigeGestionEtatEnum.Ouvert ? '#4caf50' :
                                                            litige.etat === LitigeGestionEtatEnum.Clos ? '#f44336' : '#ff9800',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold'
                                            }}>
                                            {litige.etat === LitigeGestionEtatEnum.Ouvert ? 'Ouvert' :
                                            litige.etat === LitigeGestionEtatEnum.Clos ? 'Clos' : 'En attente de paiement'}
                                            </span>
                                        </TableCell>
                                        <TableCell><Datetime withTooltip datetime={litige.dateCreation} /></TableCell>
                                        <TableCell>{litige.libelleOrigine}</TableCell>
                                        {/* <TableCell>{litige.reclamationId}</TableCell> */}
                                        <TableCell sx={{whiteSpace: 'pre-wrap'}}>{litige.commentaire}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Paper sx={{ mt: 2, p:1 }}>
                <Typography variant="h6" gutterBottom>BL de la facture {pieceGestion.facture}</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Numéro de pièce</TableCell>
                                <TableCell>Numéro de préparation</TableCell>
                                <TableCell>Référence commande</TableCell>
                                <TableCell>Date expédition</TableCell>
                                <TableCell>Date création</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>

                            <TableRow key={pieceGestion.numeroPiece}>
                                <TableCell>{pieceGestion.numeroPiece}</TableCell>
                                <TableCell>{pieceGestion.numeroPreparation}</TableCell>
                                <TableCell>{pieceGestion.referenceCommande}</TableCell>
                                <TableCell><Datetime datetime={pieceGestion.dateLivraison} /></TableCell>
                                <TableCell><Datetime datetime={pieceGestion.dateCreation} /></TableCell>
                            </TableRow>

                            {pieceGestion.piecesGestion.map((pg) => (
                                <TableRow key={pg.numeroPiece}>
                                    <TableCell>                           
                                        <Link
                                            to={`/informations/${pg.numeroPiece}`}
                                            target='_blank'
                                            underline="hover"
                                            component={RouterLink}
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {pg.numeroPiece}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{pg.numeroPreparation}</TableCell>
                                    <TableCell>{pg.referenceCommande}</TableCell>
                                    <TableCell><Datetime datetime={pg.dateLivraison} /></TableCell>
                                    <TableCell><Datetime datetime={pg.dateCreation} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
};

export default InfosFactureLitiges;
